/**
 * D6System - Système de jeu D6 (combat, dés, etc.)
 * Identique à Flutter D6System
 */

import {SimpleSystem} from '../simple/SimpleSystem';
import GameRegistry from '../GameRegistry';
import {D6ForceExtraFieldType} from './extraFields/D6ForceExtraFieldType';
import {D6LifeExtraFieldType} from './extraFields/D6LifeExtraFieldType';
import {D6DamageExtraFieldType} from './extraFields/D6DamageExtraFieldType';
import {D6PVLTConditionType} from './conditions/D6PVLTConditionType';
import {D6PVGTConditionType} from './conditions/D6PVGTConditionType';
import {D6ForceLTConditionType} from './conditions/D6ForceLTConditionType';
import {D6ForceGTConditionType} from './conditions/D6ForceGTConditionType';
import {D6DamageLTConditionType} from './conditions/D6DamageLTConditionType';
import {D6DamageGTConditionType} from './conditions/D6DamageGTConditionType';
import {D6BattleActionType} from './actions/D6BattleActionType';
import D6SlotRenderer from './D6SlotRenderer';
import D6BattleStepWidget from './widgets/D6BattleStepWidget';
import React from 'react';
import {GameState} from '../../models/GameState';
import {PlayerStep} from '../../models/PlayerStep';
import {ExtraFieldType} from '../types/ExtraFieldType';
import {BattleStepType} from './step/BattleStepType';

export class D6System extends SimpleSystem {
  private _d6ConditionsRegistered = false;
  private _adventureExtraFieldTypes: ExtraFieldType[] | null = null;

  constructor() {
    super(); // Enregistre les conditions de base
    this._registerActions();
    this._registerExtraFields();
    this._registerSlotRenderer();
    this._registerStepBuilders();
  }

  /**
   * Enregistre les actions D6
   */
  private _registerActions(): void {
    console.log('[D6System] Registering actions...');
    GameRegistry.registerActionType(new D6BattleActionType());
    console.log('[D6System] Registered D6 action: battle');
  }

  /**
   * Enregistre les types de conditions D6
   * À appeler explicitement après l'initialisation de GameRegistry
   */
  registerConditionTypes(): void {
    if (this._d6ConditionsRegistered) {
      return; // Déjà enregistrées
    }

    // Enregistrer les conditions de base depuis SimpleSystem
    super.registerConditionTypes();

    // Enregistrer les conditions D6
    GameRegistry.registerConditionType('D6_COND_PV_LT', () => new D6PVLTConditionType());
    GameRegistry.registerConditionType('D6_COND_PV_GT', () => new D6PVGTConditionType());
    GameRegistry.registerConditionType('D6_COND_FORCE_LT', () => new D6ForceLTConditionType());
    GameRegistry.registerConditionType('D6_COND_FORCE_GT', () => new D6ForceGTConditionType());
    GameRegistry.registerConditionType('D6_COND_DAMAGE_LT', () => new D6DamageLTConditionType());
    GameRegistry.registerConditionType('D6_COND_DAMAGE_GT', () => new D6DamageGTConditionType());

    this._d6ConditionsRegistered = true;
    console.log('[D6System] Registered D6 condition types: PV_LT/GT, FORCE_LT/GT, DAMAGE_LT/GT');
  }

  /**
   * Enregistre les extraFields D6
   */
  private _registerExtraFields(): void {
    console.log('[D6System] Registering extraFields...');
    // Types de base pour le joueur (adventureExtraFields)
    const forceType = new D6ForceExtraFieldType();
    const lifeType = new D6LifeExtraFieldType();
    const damageType = new D6DamageExtraFieldType();
    
    GameRegistry.registerAdventureExtraFieldType(forceType);
    GameRegistry.registerAdventureExtraFieldType(lifeType);
    GameRegistry.registerAdventureExtraFieldType(damageType);
    
    // Stocker les types d'aventure pour getAdventureExtraFieldTypes()
    this._adventureExtraFieldTypes = [forceType, lifeType, damageType];
    
    // Types _MONSTER pour les stepExtraFields (seront déclarés dans BattleStepType)
    // On les enregistre ici pour que generateValue() fonctionne
    const monsterForceType = new D6ForceExtraFieldType();
    (monsterForceType as any).typeName = 'D6_FORCE_MONSTER';
    GameRegistry.registerStepExtraFieldType(monsterForceType);
    
    const monsterLifeType = new D6LifeExtraFieldType();
    (monsterLifeType as any).typeName = 'D6_PV_MONSTER';
    GameRegistry.registerStepExtraFieldType(monsterLifeType);
    
    const monsterDamageType = new D6DamageExtraFieldType();
    (monsterDamageType as any).typeName = 'D6_DAMAGE_MONSTER';
    GameRegistry.registerStepExtraFieldType(monsterDamageType);
    
    console.log('[D6System] Registered adventure extraFields: D6_FORCE, D6_PV, D6_DAMAGE');
    console.log('[D6System] Registered step extraFields: D6_FORCE_MONSTER, D6_PV_MONSTER, D6_DAMAGE_MONSTER');
  }

  /**
   * Retourne les types d'extraFields déclarés pour l'aventure dans ce système
   * Agnostique : le GameStateHelper utilise cette méthode pour savoir quels extraFields initialiser
   */
  getAdventureExtraFieldTypes(): ExtraFieldType[] {
    // Retourner uniquement les types d'aventure (pas les _MONSTER qui sont pour les steps)
    return this._adventureExtraFieldTypes || [];
  }

  /**
   * Enregistre le renderer de slot D6
   */
  private _registerSlotRenderer(): void {
    const renderer = (gameState: GameState | null, slotIndex: number) => {
      if (!gameState) {
        return null;
      }
      return React.createElement(D6SlotRenderer, {gameState, slotIndex});
    };
    GameRegistry.registerSlotRenderer('D6', renderer);
    console.log('[D6System] Registered slot renderer for D6 system');
  }

  /**
   * Enregistre les builders de step D6
   */
  private _registerStepBuilders(): void {
    // D6_STEP_BATTLE : wrapper avec bouton "ATTAQUER" qui ouvre l'arène de combat
    GameRegistry.registerStepBuilder('D6_STEP_BATTLE', (step: PlayerStep) => {
      return React.createElement(D6BattleStepWidget, {step});
    });
    
    // Enregistrer le StepType pour que GameStateHelper puisse utiliser getStepExtraFieldTypes()
    const battleStepType = new BattleStepType();
    GameRegistry.registerStepType(battleStepType);
    
    console.log('[D6System] Registered step builder: D6_STEP_BATTLE');
  }
}
